<template>
    <!-- Messages Table -->
    <ScrollArea class="w-full border rounded-md" :class="(props.messages?.length || 0) <= 10 ? 'h-auto' : 'h-70-dynamic'">
        <Table>
            <TableHeader class="sticky top-0 bg-background z-10">
                <TableRow class="bg-muted/50">
                    <TableHead class="w-12 first:rounded-tl-md"></TableHead>
                    <TableHead>Offset</TableHead>
                    <TableHead>Partition</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead class="last:rounded-tr-md">Value</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                <!-- Skeleton rows when loading -->
                <template v-if="loading">
                    <TableRow v-for="n in 10" :key="`skeleton-${n}`">
                        <TableCell class="w-12"><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                        <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                        <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                        <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                        <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                        <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                    </TableRow>
                </template>
                <!-- Actual data rows -->
                <template v-else>
                    <TableRow
                        v-for="(message, index) in props.messages"
                        :key="index"
                        :class="{
                            'bg-muted/50': selectedMessage && selectedMessage === message
                        }"
                        class="cursor-pointer"
                        @click="selectMessage(message)">
                        <TableCell class="w-12">
                            <Button variant="ghost" size="icon" class="h-6 w-6 p-1">
                                <Minus v-if="selectedMessage && selectedMessage === message" class="h-4 w-4 text-primary" />
                                <Plus v-else class="h-4 w-4 text-primary" />
                            </Button>
                        </TableCell>
                        <TableCell class="font-mono text-xs">
                            {{ message.offset }}
                        </TableCell>
                        <TableCell class="font-mono text-xs">{{ message.partition }}</TableCell>
                        <TableCell class="font-mono text-xs">{{ message.timestamp }}</TableCell>
                        <TableCell class="font-mono text-xs truncate max-w-lg text-muted-foreground">{{ message.key }}</TableCell>
                        <TableCell class="font-mono text-xs truncate max-w-lg text-muted-foreground">{{ message.value }}</TableCell>
                    </TableRow>
                    <TableEmpty v-if="!loading && props.messages !== null && !props.messages.length" :colspan="6">
                        <TableEmptyState />
                    </TableEmpty>
                </template>
            </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
        <ScrollBar orientation="vertical" />
    </ScrollArea>

    <!-- Pagination -->
    <TablePagination
        v-if="(props.messages && props.messages.length) || page > 0"
        :current-page="page"
        :page-size="props.pageSize"
        :total-items="props.totalMessages || 0"
        :current-page-items-count="props.messages?.length || 0"
        item-type="messages"
        :loading="loading"
        :show-items-per-page="true"
        @page-change="onPageChange"
        @page-size-change="onPageSizeChange" />
</template>

<script setup>
import { ref, watch } from 'vue';
import TablePagination from '@/components/TablePagination.vue';
import { Plus, Minus } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import _ from 'lodash';

const props = defineProps({
    messages: {
        type: Array,
        default: () => []
    },
    totalMessages: {
        type: Number,
        default: 0
    },
    pageSize: {
        type: Number,
        default: 10
    },
    page: {
        type: Number,
        default: 0
    },
    loading: Boolean,
    unselectAll: {
        type: Boolean,
        default: false
    },
    sort: {
        type: String,
        default: 'desc'
    }
});

const emit = defineEmits(['select-message', 'page-change', 'page-size-change']);

watch(
    () => props.page,
    (newVal) => {
        page.value = newVal;
    }
);

const page = ref(props.page);
const selectedMessage = ref(null);

const onPageChange = (newPage) => {
    let direction = 'forward';
    if (newPage < page.value) {
        direction = 'backward';
    }

    page.value = newPage;

    emit('page-change', { page: newPage, direction });
};

watch(
    () => props.unselectAll,
    (newVal) => {
        if (newVal) {
            selectedMessage.value = null;
        }
    }
);

const selectMessage = (message) => {
    if (selectedMessage.value === message) {
        selectedMessage.value = null;
    } else {
        selectedMessage.value = message;
    }

    emit('select-message', selectedMessage.value);
};

const onPageSizeChange = (newSize) => {
    emit('page-size-change', newSize);
};
</script>
